import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useStore } from "@/hooks/useStore";
import { Panel, PageTitle } from "@/components/ui/Panel";
import { Badge } from "@/components/ui/Badge";

export function Home() {
  const { user, profile } = useAuth();
  const { state, ready } = useStore();

  const counts = {
    cards: state.cards.length,
    decks: state.decks.length,
    finalized: state.cards.filter((c) => c.status === "Finalized").length,
    testing: state.cards.filter((c) => c.status === "Testing").length,
    draft: state.cards.filter((c) => c.status === "Draft").length,
  };

  return (
    <div className="space-y-6">
      <div>
        <PageTitle>Card Design Tool</PageTitle>
        <p className="mt-1 text-sm text-[#64748b]">
          Welcome{profile ? `, ${profile.display_name}` : ""}. Build, test, balance.
        </p>
      </div>

      {!user && (
        <Panel>
          <p className="text-sm">
            <Link className="gm-link" to="/signin">Sign in</Link> to edit cards and play online.
          </p>
        </Panel>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Total cards" value={counts.cards} />
        <Stat label="Decks" value={counts.decks} />
        <Stat label="Finalized" value={counts.finalized} tone="green" />
        <Stat label="Testing / Draft" value={`${counts.testing} / ${counts.draft}`} tone="amber" />
      </div>

      <Panel>
        <h2 className="font-display text-lg font-semibold mb-2">Quick links</h2>
        <ul className="space-y-1 text-sm">
          <li><Link className="gm-link" to="/cards">All Cards</Link></li>
          {profile?.role === "admin" && <li><Link className="gm-link" to="/cards/add">Add a new card</Link></li>}
          {profile?.role === "admin" && <li><Link className="gm-link" to="/decks">Decks</Link></li>}
          <li><Link className="gm-link" to="/playtest">Playtest</Link></li>
          <li><Link className="gm-link" to="/glossary">Glossary</Link></li>
        </ul>
      </Panel>

      {!ready && <p className="text-xs text-[#64748b]">Loading state…</p>}
    </div>
  );
}

function Stat({ label, value, tone = "default" }: { label: string; value: number | string; tone?: "default" | "amber" | "green" }) {
  return (
    <div className="gm-panel p-4">
      <p className="text-[11px] uppercase tracking-wider text-[#64748b]">{label}</p>
      <p className="mt-1 font-display text-2xl font-bold">{value}</p>
      {tone !== "default" && <Badge tone={tone} className="mt-2">{tone}</Badge>}
    </div>
  );
}
